import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// routes
import { useSearchParams, useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { INTERNAL_SERVER_ERROR, UNKNOWN_ERROR } from 'src/utils/axios';
//
import { Box } from '@mui/material';
import GoogleButton from './google-button';

// ----------------------------------------------------------------------

const OAUTH_ERRORS: Record<string, string> = {
  oauth_failed: 'Google sign-in failed. Please try again.',
  oauth_no_email: 'Google did not share an email address for your account.',
};

export default function LoginForm() {
  const { login } = useAuthContext();

  const router = useRouter();

  const [errorMsgs, setErrorMsgs] = useState<BackendValidationError[]>([]);

  const searchParams = useSearchParams();

  const returnTo = searchParams.get('returnTo');

  const oauthError = searchParams.get('error');

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await login?.(data.email, data.password);
      router.push(returnTo || PATH_AFTER_LOGIN);
    } catch (error) {
      setErrorMsgs(
        error?.response?.data || [
          { message: error?.response?.status === 500 ? INTERNAL_SERVER_ERROR : UNKNOWN_ERROR },
        ]
      );
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2.5}>
        <Stack spacing={0.75}>
          <Box sx={{ justifyContent: 'center', display: 'flex' }}>
            <Box
              component="img"
              src="/logo/logo_ifta.png"
              alt="Counting Trucks IFTA"
              data-testid="ifta-logo"
              sx={{ width: 230, height: 'auto' }}
            />
          </Box>

          <Box sx={{ color: 'text.secondary', pt: 3, textAlign: 'center' }}>
            Quarterly fuel tax for your fleet.
          </Box>
        </Stack>

        {oauthError && (
          <Alert severity="error">{OAUTH_ERRORS[oauthError] || OAUTH_ERRORS.oauth_failed}</Alert>
        )}

        {errorMsgs.map((errorMsg, index) => (
          <Alert key={index} severity="error">
            {errorMsg.message}
          </Alert>
        ))}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={password.onToggle} edge="end">
                  <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          fullWidth
          color="secondary"
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Login
        </LoadingButton>

        <Divider>or</Divider>

        <GoogleButton />

        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          New to Counting Trucks?{' '}
          <Link
            component={RouterLink}
            href={paths.auth.register}
            variant="subtitle2"
            sx={{
              color: 'text.primary',
              textDecorationColor: 'inherit',
              textDecoration: 'underline',
            }}
          >
            Create an account
          </Link>
        </Typography>
      </Stack>
    </FormProvider>
  );
}
